import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from 'src/auth/hooks';
import { ModuleEntitlementsService } from 'src/services/module-entitlements-service';
import { ModuleEntitlementsContext } from './module-entitlements-context';

// ----------------------------------------------------------------------

type ModuleEntitlementsProviderProps = {
    children: React.ReactNode;
};

export const ModuleEntitlementsProvider = ({
    children,
}: ModuleEntitlementsProviderProps) => {
    const { authenticated } = useAuthContext();

    const [entitlements, setEntitlements] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchEntitlements = useCallback(async () => {
        setLoading(true);
        setLoaded(false);
        setError(null);

        try {
            const docSurveyEntitled = await ModuleEntitlementsService.getDocSurveyEntitlement();
            setEntitlements({ DocSurvey: !!docSurveyEntitled });
        } catch (fetchError) {
            console.error(fetchError);
            const message = fetchError instanceof Error
                ? fetchError.message
                : 'Failed to load module entitlements.';
            setError(message);
        } finally {
            setLoading(false);
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        if (!authenticated) {
            setEntitlements({});
            setLoading(false);
            setLoaded(true);
            setError(null);
            return;
        }
        fetchEntitlements();
    }, [authenticated, fetchEntitlements]);

    const getEntitlement = useCallback(
        (moduleKey: string) => entitlements[moduleKey],
        [entitlements]
    );

    const memoizedValue = useMemo(
        () => ({
            entitlements,
            loading,
            loaded,
            error,
            getEntitlement,
            refresh: fetchEntitlements,
        }),
        [entitlements, loading, loaded, error, getEntitlement, fetchEntitlements]
    );

    return (
        <ModuleEntitlementsContext.Provider value={memoizedValue}>
            {children}
        </ModuleEntitlementsContext.Provider>
    );
};
